import React, { useState, useEffect, useRef } from 'react';
import { Link as RouterLink, useLocation, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from "react-helmet";
import ConsensusReviews from '../ConsensusReviews';
import { ChevronLeft, Edit } from '@mui/icons-material';
import { Button } from '@mui/material';
import '../../index.css';

import BreadCrumbs from '../BreadCrumbs';
import APIUtils from '../../utils/APIUtils';
import ReviewStatus from '../../config/ReviewStatus';
import SessionConstants from '../../config/SessionConstants';
import Loading from '../Loading';
import DateUtils from '../../utils/DateUtils';

import HGVSNames from '../HGVSNames';
import VariantInfoGrid from '../VariantInfoGrid';
import ConfirmationModal from '../../uiComponents/ConfirmationModal';
export default function VariantDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const { variantId: paramVariantId } = useParams();
  const input = useRef();

  // Extract state from location
  const {
    breadCrumbs: passedBreadCrumbs = [],
    justAddedInterpretation,
    variantId: stateVariantId,
    variantName: stateVariantName,
    expected: stateExpected,
    completed: stateCompleted,
    inProgress: stateInProgress,
  } = location.state || {};

  // Prefer state variantId, fallback to URL param
  const variantId = stateVariantId || paramVariantId;

  // Whether the user arrived here from the Explore page (per the breadcrumb trail)
  const cameFromExplore = passedBreadCrumbs.some((crumb) => crumb && crumb.name === 'Explore');

  const [breadCrumbs, setBreadCrumbs] = useState([]);
  const [variantName, setVariantName] = useState(stateVariantName || '');
  const [expected, setExpected] = useState(stateExpected || null);
  const [completed, setCompleted] = useState(stateCompleted || null);
  const [inProgress, setInProgress] = useState(stateInProgress || null);
  const [summary, setSummary] = useState({});
  const [requiredInterpretations, setRequiredInterpretations] = useState(3);
  const [modalVariantsIsOpen, setModalVariantsIsOpen] = useState(false);
  const [interpretations, setInterpretations] = useState([]);
  const [hasUserInterpretation, setHasUserInterpretation] = useState(false);
  const [userInterpretation, setUserInterpretation] = useState({});
  const [dataExists, setDataExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [summaryMsg, setSummaryMsg] = useState('');
  const [loadMe, setLoadMe] = useState(true);
  const [justAdded, setJustAdded] = useState(justAddedInterpretation || null);
  const [programId, setProgramId] = useState(null);
  const [reviewStatus, setReviewStatus] = useState(null);
  const [createdDate, setCreatedDate] = useState('');
  const [lastEdited, setLastEdited] = useState('');
  const [closedDate, setClosedDate] = useState('');
  const [gene, setGene] = useState('');
  const [condition, setCondition] = useState('');
  const [variantType, setVariantType] = useState('');
  const [annotationLastUpdated, setAnnotationLastUpdated] = useState('');
  const [annotationLastDownloaded, setAnnotationLastDownloaded] = useState('');
  const [currentActivity, setCurrentActivity] = useState(null);
  const [previousActivity, setPreviousActivity] = useState(null);
  const [annotationAvailable, setAnnotationAvailable] = useState(false);
  const [annotationConfirmationOpen, setAnnotationConfirmationOpen] = useState(false);
  const [annotationRefreshPending, setAnnotationRefreshPending] = useState(false);
  const [annotationRefreshResult, setAnnotationRefreshResult] = useState(null);
  const annotationRefreshInFlight = useRef(false);

  async function refreshAnnotation() {
    if (annotationRefreshInFlight.current) return;
    annotationRefreshInFlight.current = true;
    setAnnotationConfirmationOpen(false);
    setAnnotationRefreshPending(true);
    try {
      await APIUtils.downloadVariantAnnotation({
        variant_object_id: variantId,
        programId,
        userEmail: sessionStorage.getItem(SessionConstants.USER_EMAIL),
      });
      setAnnotationRefreshResult({
        started: true,
        title: 'Variant annotation download started!',
        message: '',
      });
    } catch {
      setAnnotationRefreshResult({
        started: false,
        title: 'Variant annotation download could not be started.',
        message: 'Your existing annotation data has not changed. Please try again.',
      });
    } finally {
      annotationRefreshInFlight.current = false;
      setAnnotationRefreshPending(false);
    }
  }

  // Load passed parameters and review status on mount or when variantId changes
  useEffect(() => {
    // Breadcrumb logic
    const isJustAdded = justAddedInterpretation === true;
    const crumbExists = passedBreadCrumbs.some(
      (crumb) => crumb.name === 'VariantDetails' && crumb.variantId === variantId
    );
    const updatedBreadCrumbs = isJustAdded || crumbExists
      ? passedBreadCrumbs
      : [...passedBreadCrumbs, { name: 'VariantDetails', variantId, variantName }];

    setBreadCrumbs(updatedBreadCrumbs);
    setProgramId(sessionStorage.getItem(SessionConstants.PROGRAM_ID));
    setVariantName(stateVariantName || '');
    setJustAdded(isJustAdded);

    setExpected(stateExpected || null);
    setCompleted(stateCompleted || null);
    setInProgress(stateInProgress || null);

    // Gate the page render on the async work that determines the initial layout
    // (including whether the annotation button is enabled) so content appears in
    // its final state instead of flickering as each request resolves.
    setLoading(true);
    Promise.all([
      getInterpretationStatus(variantId),
      checkAnnotationData(variantId),
    ]).finally(() => setLoading(false));
    getReviewStatus(variantId);
    getProgramActivity(variantId);
  }, [variantId, stateVariantName, justAddedInterpretation, stateExpected, stateCompleted, stateInProgress]);

  // Get the current user's interpretation
  async function getInterpretationStatus(variantId) {
    try {
      const response = await APIUtils.getVariantInterpretation(
        sessionStorage.getItem(SessionConstants.USER_EMAIL),
        variantId
      );
      if (response) {
        setHasUserInterpretation(true);
        setUserInterpretation(response);
        setSummaryMsg(
          response.interpretationStatus !== 'Completed'
            ? 'You have an interpretation for this variant review assigned to you.'
            : 'You have submitted an interpretation for this variant review.'
        );
      }
    } catch (err) {
      // A variant may not have a review/interpretation; still allow the page to load
      console.error('Error fetching interpretation status:', err);
    }
  }

  // Get the review status for the given variant
  async function getReviewStatus(variantId) {
    const programId = sessionStorage.getItem('programId');
    try {
      const response = await APIUtils.getReview(programId, variantId);
      if (response) {
        let expected = response.requiredInterpretations;
        let completed = 0;
        let inProgress = 0;
        for (let interpretation of Object.values(response.variantInterpretations)) {
          if (interpretation.interpretationStatus === 'In Progress') inProgress += 1;
          if (interpretation.interpretationStatus === 'Completed') completed += 1;
        }
        setProgramId(response.programId);
        setVariantName(response.variantName);
        setReviewStatus(response.reviewStatus);
        setCreatedDate(DateUtils.FormatLocalDate(response.createdDate));
        setLastEdited(DateUtils.FormatLocalDate(response.lastEditedDate));
        setClosedDate(DateUtils.FormatLocalDate(response.closedDate));
        setGene(response.geneId);
        setCondition(response.conditions);
        setVariantType(response.annotationInfo?.variantType || '');
        setAnnotationLastUpdated(response.annotationInfo?.lastUpdatedDate || '');
        setExpected(expected);
        setCompleted(completed);
        setInProgress(inProgress);
      }
    } catch (err) {
      // A variant may not have a review yet; the details page should still load
      console.error('Error fetching review status:', err);
    }
  }

  // Get program activity (current + previous review) for the variant
  async function getProgramActivity(variantId) {
    const programId = sessionStorage.getItem('programId');
    try {
      const response = await APIUtils.getProgramActivity(programId, variantId);
      if (response) {
        const current = response.currentActivity;
        const previous = response.previousActivity;
        setCurrentActivity(current && Object.keys(current).length > 0 ? current : null);
        setPreviousActivity(previous && Object.keys(previous).length > 0 ? previous : null);
      }
    } catch (err) {
      console.error('Error fetching program activity:', err);
    }
  }

  // Determine whether annotation data is available for this variant. Mirrors the
  // check used by the VariantAnnotationData page so the button state matches what
  // the page would actually render.
  async function checkAnnotationData(variantId) {
    try {
      const response = await APIUtils.getAnnotationsForVariant(variantId);
      setAnnotationLastDownloaded(DateUtils.FormatLocalDate(response?.dateTimeCreated));
      setAnnotationAvailable(
        typeof response !== 'string' &&
        !!response &&
        Array.isArray(response.categories) &&
        response.categories.length > 0
      );
    } catch (err) {
      // No annotation data available for this variant; keep the button disabled
      console.error('Error fetching annotation data availability:', err);
      setAnnotationAvailable(false);
      setAnnotationLastDownloaded('');
    }
  }

  const closeModal = () => setModalVariantsIsOpen(false);

  const handleChange = (event) => setExpected(parseInt(event.target.value));

  // Formatting
  const variantNameDisplay = variantName !== undefined ? variantName : '';

  return (
    <div className="variantDetails">
      <Loading isLoading={loading} />
      <Helmet>
        <title>ED3N - Variant Details</title>
      </Helmet>
      <ConfirmationModal
        isOpen={annotationConfirmationOpen}
        title="Re-Download Annotation Data"
        message="This will re-download the annotation data for this variant. Download progress can be viewed in the Workflow Runs page. Proceed?"
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirmation={refreshAnnotation}
        onCancel={() => setAnnotationConfirmationOpen(false)}
      />
      <ConfirmationModal
        isOpen={!!annotationRefreshResult}
        title={annotationRefreshResult?.title || ''}
        message={annotationRefreshResult?.message || ''}
        confirmText={annotationRefreshResult?.started ? 'Show Workflow Runs' : 'Close'}
        cancelText="Close"
        onConfirmation={() => {
          const started = annotationRefreshResult?.started;
          setAnnotationRefreshResult(null);
          if (started) {
            navigate('/workflowruns', { state: { programId } });
          }
        }}
        onCancel={annotationRefreshResult?.started ? () => setAnnotationRefreshResult(null) : undefined}
      />
      <BreadCrumbs breadCrumbs={breadCrumbs} />
      <div className="button-left-right-bar">
        <div>
          {cameFromExplore ? (
            <Button
              id="backButton"
              aria-label="Go Back to Explore"
              startIcon={<ChevronLeft />}
              variant="contained"
              color="primary"
              onClick={() => {
                // Restore the Explore search/table state, just like a browser back
                sessionStorage.setItem('exploreRestoreOnReturn', '1');
                navigate(-1);
              }}
            >
              Back to Explore
            </Button>
          ) : (
            <RouterLink
              tabIndex="-1"
              to="/programsummary"
              state={{
                programId: programId,
              }}
            >
              <Button
                id="backButton"
                aria-label="Go Back to Program Summary"
                startIcon={<ChevronLeft />}
                variant="contained"
                color="primary"
              >
                Back to Program Summary
              </Button>
            </RouterLink>
          )}
        </div>
      </div>
      <section>
        <header>
            <h1 className="pageTitle">Variant Details</h1>
            {!loading && (
              <VariantInfoGrid 
                variantName={variantNameDisplay}
                gene={gene}
                condition={condition}
                hgvsNames={<HGVSNames variantId={variantId} />}
                variantType={variantType}
                annotationLastUpdated={annotationLastDownloaded || annotationLastUpdated}
                annotationDisabled={!annotationAvailable}
                annotationDisabledMessage="No annotation data available."
                onRefreshAnnotationData={() => setAnnotationConfirmationOpen(true)}
                annotationRefreshPending={annotationRefreshPending}
                onViewAnnotationData={() =>
                  navigate('/variantannotationdata', {
                    state: {
                      variantObjectId: variantId,
                      variantName: variantNameDisplay,
                      breadCrumbs: breadCrumbs,
                    },
                  })
                }
              />
            )}
        </header>
      </section>
      <div className="button-bar"></div>
      {!loading ? (
        <>
          <section>
            <div className="section">
              <h2 className="sectionHeading">Your Activity</h2>
              <div className="sectionContent">
                {hasUserInterpretation ? (
                  <>
                    {summaryMsg}
                    <br /><br />
                    Interpretation Classification: {userInterpretation.programClassification != null ? userInterpretation.programClassification : 'None Selected'}<br />
                    Date Last Edited: {userInterpretation.interpretedDate
                      ? DateUtils.FormatLocalDate(userInterpretation.interpretedDate)
                      : ''
                    }
                    <br /><br />
                    {userInterpretation.interpretationStatus !== 'Completed' ? (
                      <RouterLink
                        tabIndex="-1"
                        to="/addinterpretation"
                        state={{
                          summary: summary,
                          breadCrumbs: breadCrumbs,
                          programId: programId,
                          gene: gene,
                          variantId: variantId,
                          variantName: variantName,
                          variantInterpretationId: userInterpretation.variantInterpretationId
                        }}
                      >
                        <Button
                          aria-label="edit your interpretation"
                          startIcon={<Edit />}
                          variant="contained"
                          color="primary"
                        >
                          Edit Your Interpretation
                        </Button>
                      </RouterLink>
                    ) : ''}
                  </>
                ) : (
                  <>No interpretation assigned to you.</>
                )}
              </div>
            </div>
          </section>
          <section>
            <div className="section">
              <h2 className="sectionHeading">Your Program Review Activity</h2>
              <div className="sectionContent" style={{ display: 'flex', gap: '2rem' }}>
                <div style={{ flex: 1 }}>
                  <h3>Current Active Review</h3>
                  {currentActivity ? (
                    <div>
                      Review Version: {currentActivity.version || '--'}<br />
                      Review Status: {currentActivity.status || '--'}<br />
                      Interpretation Status: {currentActivity.interpretationStatus || '--'}
                    </div>
                  ) : (
                    <div>No Current Active Review</div>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <h3>Latest Completed Consensus Review</h3>
                  {previousActivity ? (
                    <div>
                      Review Version: {previousActivity.version || '--'}<br />
                      Date Published: {previousActivity.datePublish ? DateUtils.FormatLocalDate(previousActivity.datePublish) : '--'}<br />
                      Classification: {previousActivity.classification || '--'}<br />
                      Tags: {Array.isArray(previousActivity.tags) && previousActivity.tags.length > 0 ? previousActivity.tags.join(', ') : '--'}
                    </div>
                  ) : (
                    <div>No Completed Consensus Reviews</div>
                  )}
                </div>
              </div>
            </div>
          </section>
          <section>
            <div className="section">
              <h2 className="sectionHeading">Final Consensus Reviews by All Programs</h2>
              <div className="sectionContent">
                {loadMe || justAdded ? (
                  <ConsensusReviews variantId={variantId} />
                ) : ('')}
              </div>
            </div>
          </section>
        </>
      ) : (
        <div className="noData"></div>
      )}
    </div>
  );
}